/**
 * RackFormElegant — Odoo-style full-screen detail view
 * Layout: fixed overlay (right of sidebar, below navbar)
 *   ├─ TopBar   : ← Back | breadcrumb | Save | Discard | Delete
 *   ├─ SmartBar : SmartButtons (servers, occupancy, free U, status)
 *   └─ Body (flex row, internal scroll)
 *       ├─ Main  : record title + tabs (General / Dimensiones / Hardware / Servidores)
 *       └─ Right sidebar : chatter / notes
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  SparklesIcon,
  ServerStackIcon,
  CubeIcon,
  BoltIcon,
  TableCellsIcon,
  RectangleStackIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import DocSmartButton from './DocSmartButton';
import LocationTreeSelect from './common/LocationTreeSelect';
import { buildLocationMap, locationPath } from '../utils/locationTree';
import CiActivityPanel from './common/CiActivityPanel';
import Dialog from './common/Dialog';
import CloneDialog from './common/CloneDialog';
import PromoteRackDialog from './PromoteRackDialog';
import HelpDot from '../academy/HelpDot';
import '../styles/DetailView.css';
import DetailCrumb from './DetailCrumb';
import FormCardsShell from './FormCardsShell';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';

// Tipos de elemento que pueden montarse en un rack → etiqueta legible.
const ELEMENT_TYPE_LABEL = {
  server: 'Servidor', storage: 'Cabina de almacenamiento', ups: 'SAI', pdu: 'PDU',
  switch: 'Switch', router: 'Router', firewall: 'Firewall', panel: 'Patch panel',
  ont: 'ONT', tray: 'Bandeja', chassis: 'Chasis', ap: 'Punto de acceso',
  phone: 'Teléfono IP', printer: 'Impresora', workstation: 'Estación de trabajo',
  outlet: 'Toma de pared',
};

function RackFormElegant({ rack, locationId, companyId, initialMode = 'create', initialStatus = 'live', onClose, onSave, variant = 'classic', siblingRacks = [], onNavigate }) {
  const isCards = variant === 'cards';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isExisting = !!rack?.id;
  // Modo lectura por defecto en las fichas existentes (estándar del CMDB); el
  // alta y el botón «Editar» del listado piden `initialMode="edit"`.
  const [editing, setEditing] = useState(!isExisting || initialMode === 'edit');
  const [showClone, setShowClone] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  // Pasar un borrador a producción: el estado no se cambia editando (el backend
  // lo rechaza), va por `/promote`, que asigna código real y ubicación.
  const [showPromote, setShowPromote] = useState(false);
  // Volver a borrador: acción explícita y auditada (conserva código y
  // numeración). El estado no se cambia con el switch de edición.
  const [showRevert, setShowRevert] = useState(false);
  const [reverting, setReverting] = useState(false);

  const withFallback = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);

  // ── Form state ──
  const [formData, setFormData] = useState({
    location_id: locationId || null,
    code: '',
    name: '',
    description: '',
    height_u: 42,
    width_mm: 600,
    depth_mm: 800,
    manufacturer: '',
    model: '',
    serial_number: '',
    is_active: true,
    status: initialStatus,
    design_group: '',
  });
  const [isDirty, setIsDirty] = useState(false);
  // En un alta el código copia al nombre hasta que se escriba a mano (o lo
  // genere ✨): normalmente son el mismo texto.
  const [codeLocked, setCodeLocked] = useState(false);
  /** Último nombre guardado. No se puede comparar contra `rack.name`: el listado
   *  puede tardar en refrescar la fila y el título se quedaría marcado como
   *  modificado para siempre. */
  const [committedName, setCommittedName] = useState(rack?.name || '');

  // ── Data state ──
  const [locations, setLocations] = useState([]);
  const [servers, setServers] = useState([]);
  /** Todos los elementos del rack (servidores, cabinas, SAI, PDU, switches…). */
  const [rackElements, setRackElements] = useState([]);

  // ── UI state ──
  const [activeTab, setActiveTab] = useState('general');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  // ── Load data ──
  useEffect(() => {
    if (rack) {
      setFormData({ ...rack });
      setCommittedName(rack.name || '');
    }
  }, [rack]);

  useEffect(() => {
    if (!companyId) return;
    api.getLocations('', companyId)
      .then(r => setLocations(r.data || []))
      .catch(() => {});
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !rack?.id) return;
    api.getServers(companyId, { rack_id: rack.id })
      .then(r => setServers(r.data || []))
      .catch(() => {});
    api.getElements(companyId, { rack_id: rack.id })
      .then(r => setRackElements(r.data || []))
      .catch(() => setRackElements([]));
  }, [companyId, rack?.id]);

  // ── SmartButton computed values ──
  const occupiedU = servers.reduce((acc, s) => acc + (s.size_u || s.height_u || 1), 0);
  const freeU = Math.max(0, formData.height_u - occupiedU);
  const occupancyPct = formData.height_u > 0
    ? Math.round((occupiedU / formData.height_u) * 100)
    : 0;
  const mapaUbicaciones = useMemo(() => buildLocationMap(locations), [locations]);
  const ubicacionRuta = formData.location_id ? locationPath(mapaUbicaciones, formData.location_id) : '';

  // ── Pasar de ficha sin volver al listado ──
  // El orden es el del listado tal y como se está viendo (con sus filtros y su
  // ordenación), que es lo que espera quien va pasando fichas.
  const rackIndex = isExisting ? siblingRacks.findIndex(r => r.id === rack.id) : -1;

  // Cambios sin guardar al pasar de ficha o cerrar. `handleSubmit` se define
  // más abajo: la función se evalúa al pulsar, no al montar.
  const navGuard = useNavGuard({
    dirty: isDirty || (formData.name || '') !== committedName,
    saving,
    entity: 'este rack',
    actionLabel: 'pasas a otro rack o cierras la ficha',
    save: () => handleSubmit(null, { force: true, keepEditing: editing }),
  });

  const irARack = (destino) => {
    if (!destino || saving) return;
    navGuard.request(() => {
      setIsDirty(false);
      setError(null);
      onNavigate?.(destino);
    });
  };

  // ── Handlers ──
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'code') setCodeLocked(true);
    setFormData(prev => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked
          : type === 'number' ? (value === '' ? '' : parseInt(value, 10))
          : value,
      };
      if (name === 'name' && !isExisting && !codeLocked) next.code = value;
      return next;
    });
    setIsDirty(true);
  };

  const handleDiscard = () => {
    // En un rack existente, descartar vuelve a modo LECTURA (así siempre hay
    // salida de la edición aunque no se haya tocado nada). En un alta, cierra.
    if (rack) {
      // Se restaura el ÚLTIMO NOMBRE GUARDADO, no el que traiga la fila del
      // listado: si se acaba de renombrar, ese prop todavía puede ser el viejo
      // y «descartar» devolvería el nombre anterior.
      setFormData({ ...rack, name: committedName });
      setEditing(false);
    } else {
      onClose();
    }
    setIsDirty(false);
    setError(null);
  };

  const handleSubmit = async (e, { force = false, keepEditing = false } = {}) => {
    if (e?.preventDefault) e.preventDefault();
    // `force`: guardar solo el nombre desde el título en reposo (Enter), sin
    // haber entrado antes en modo edición.
    if (!editing && !force) return false;
    const isDraft = formData.status === 'draft';
    // Un borrador puede vivir sin ubicación: todavía se está diseñando. Al
    // pasarlo a producción sí se exige (lo valida el backend).
    if (!isDraft && !formData.location_id) {
      setError(withFallback('rack.location_required', 'La ubicación es obligatoria'));
      return false;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...formData,
        location_id: formData.location_id ? Number(formData.location_id) : null,
      };
      const result = isExisting
        ? await api.updateRack(rack.id, payload, companyId)
        : await api.createRack(payload.location_id, payload, companyId);

      if (result.success) {
        setIsDirty(false);
        // El nombre guardado pasa a ser el original del título inline.
        setCommittedName(payload.name || '');
        // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
        // con id, así se encienden las pestañas que necesitan estar guardado.
        onSave?.(isExisting ? null : registroGuardado(result));
        if (isExisting && !keepEditing) {
          // Se guarda y la ficha vuelve a lectura, como en el resto del CMDB.
          setEditing(false);
        }
        return true;
      } else {
        setError(result.message || 'Error al guardar');
        return false;
      }
    } catch (err) {
      setError(err.message || 'Error al guardar');
      return false;
    } finally {
      setSaving(false);
    }
  };

  /* ── Título con edición inline ─────────────────────────────────────────
   * El nombre solo cuenta como cambio si difiere del guardado, y se confirma
   * con el mismo `handleSubmit` de siempre (misma llamada a la API y mismos
   * avisos). Enter confirma y sale; Esc devuelve el valor original. */
  const nameDirty = (formData.name || '') !== committedName;
  const titleActionsVisible = nameDirty || editing;

  const commitTitle = async () => {
    if (!nameDirty && !isDirty) return;
    await handleSubmit(null, { force: true });
  };

  const restoreTitle = () => {
    setFormData(prev => ({ ...prev, name: committedName }));
    if (!editing) setIsDirty(false);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (nameDirty || (editing && isDirty)) commitTitle();
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      restoreTitle();
      e.currentTarget.blur();
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const result = await api.deleteRack(rack.id, companyId);
      setShowDelete(false);
      if (result.success) {
        onSave?.();
        onClose();
      } else {
        setError(result.message || 'Error al eliminar');
      }
    } catch (err) {
      setError(err.message || 'Error al eliminar');
      setShowDelete(false);
    } finally {
      setSaving(false);
    }
  };

  const openCloneModal = () => {
    setCloneName(`${formData.name || 'Rack'} (copia)`);
    setCloneError('');
    setShowClone(true);
  };

  const handleCloneConfirm = async () => {
    const name = cloneName.trim();
    if (!name || cloning) return;
    setCloning(true);
    setCloneError('');
    try {
      await api.cloneRack(rack.id, { name }, companyId);
      setShowClone(false);
      onSave?.();
      onClose();
    } catch (err) {
      setCloneError(err.message || 'No se pudo copiar el rack');
    } finally {
      setCloning(false);
    }
  };

  const handleRevertToDraft = async () => {
    setReverting(true);
    setError(null);
    try {
      const result = await api.revertRackToDraft(rack.id, companyId);
      const actualizado = result?.data;
      setFormData(prev => ({ ...prev, ...(actualizado || {}), status: 'draft' }));
      setShowRevert(false);
      setIsDirty(false);
      setEditing(false);
      onSave?.(null);
    } catch (err) {
      setError(err.message || 'No se pudo volver a borrador');
      setShowRevert(false);
    } finally {
      setReverting(false);
    }
  };

  const handleSuggest = async () => {
    if (!companyId) return;
    setSuggesting(true);
    setError(null);
    try {
      const resp = await api.suggestName({
        type: 'rack', companyId,
        locationId: formData.location_id || locationId || undefined,
        // Reserva el número: dos clics seguidos no devuelven el mismo código.
        consume: true,
      });
      const s = resp?.data?.suggested_name;
      if (s) {
        setCodeLocked(true);
        setFormData(prev => ({ ...prev, code: s, name: prev.name || s }));
        // Sin esto la ficha no se marcaba como modificada y el ✓ seguía
        // deshabilitado: se podía generar pero no guardar.
        setIsDirty(true);
      }
    } catch (err) {
      setError(err.message || 'No se pudo sugerir un nombre');
    }
    finally { setSuggesting(false); }
  };


  const tabs = isCards
    ? [
        { key: 'general',   label: 'General' },
        { key: 'elementos', label: `Elementos (${rackElements.length || servers.length})` },
      ]
    : [
        { key: 'general', label: 'General' },
        { key: 'dimensiones', label: 'Dimensiones' },
        { key: 'hardware', label: 'Hardware' },
        { key: 'servidores', label: `Servidores (${servers.length})` },
      ];

  return (
    <div className="dv-overlay">
      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon />
          Racks
        </button>
        <DetailCrumb label={formData.name || 'Nuevo rack'} />

        {/* Contador para pasar de ficha sin volver al listado. El orden y el
            total son los del listado tal y como se está viendo. */}
        <FormPager
          items={siblingRacks}
          index={rackIndex}
          label="Rack"
          disabled={saving}
          onChange={irARack}
        />
        {navGuard.dialog}

        <div className="dv-topbar-spacer" />

        {error && (
          <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            ⚠ {error}
          </span>
        )}

        <div className="dv-topbar-actions">
          {/* En la ficha clásica el estado sigue siendo la píldora de siempre. En
              tarjetas los dos estados bajan a la cabecera (ver `sf2-head-states`). */}
          {!isCards && (
            <span className={`dv-status-badge ${formData.is_active ? 'dv-status-active' : 'dv-status-inactive'}`}>
              {formData.is_active ? '● Activo' : '● Inactivo'}
            </span>
          )}

          {/* El diseñador está en la barra inteligente de la ficha clásica, que en
              tarjetas no existe. Sin él no había forma de abrir la vista frontal
              y trasera. Los documentos van con los chips de la cabecera. */}
          {isCards && isExisting && (
            <button
              className="dv-btn dv-btn-secondary"
              type="button"
              onClick={() => navigate(`/infrastructure/rack/${rack.id}`)}
              title="Abrir el diseñador del rack: vista frontal y trasera"
            >
              <TableCellsIcon />
              Diseño
            </button>
          )}

          {!editing && (
            <>
              <button
                className="dv-btn dv-btn-secondary"
                type="button"
                onClick={openCloneModal}
                title="Copiar este rack con su configuración"
              >
                <DocumentDuplicateIcon />
                Copiar
              </button>
              <button
                className="dv-btn dv-btn-secondary"
                type="button"
                onClick={() => setEditing(true)}
                title="Editar la ficha"
              >
                <PencilSquareIcon />
                Editar
              </button>
            </>
          )}

          {editing && !isCards && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
              <XMarkIcon />
              Descartar
            </button>
          )}

          {editing && !isCards && (
            <button
              className="dv-btn dv-btn-primary"
              type="button"
              onClick={handleSubmit}
              disabled={saving || !isDirty}
            >
              {saving ? <span className="dv-spinner" /> : <CheckIcon />}
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          )}

          {isExisting && (
            <button
              className="dv-btn dv-btn-danger"
              type="button"
              onClick={() => setShowDelete(true)}
              disabled={saving}
              title="Eliminar el rack"
            >
              <TrashIcon />
            </button>
          )}
        </div>
      </div>

      {/* ── Copiar rack (diálogo común) ── */}
      {showClone && (
        <CloneDialog
          title="Copiar rack"
          defaultName={cloneName}
          hint="Se copiarán las medidas, el fabricante, el modelo, las notas y la ubicación. No se copiarán el código ni el número de serie, y el rack nuevo nace vacío (sin elementos montados ni paneles ciegos)."
          busy={cloning}
          error={cloneError}
          onClose={() => setShowClone(false)}
          onConfirm={handleCloneConfirm}
        />
      )}

      {/* ── Pasar un borrador a producción (diálogo común) ── */}
      {showPromote && isExisting && (
        <PromoteRackDialog
          rack={rack}
          companyId={companyId}
          onClose={() => setShowPromote(false)}
          onPromoted={(payload) => {
            setShowPromote(false);
            const promovido = payload?.data;
            if (promovido) {
              setFormData(prev => ({ ...prev, ...promovido }));
              setCommittedName(promovido.name || '');
            }
            setIsDirty(false);
            setEditing(false);
            onSave?.(null);
          }}
        />
      )}

      {/* ── Volver a borrador (acción explícita: conserva código y numeración) ── */}
      {showRevert && isExisting && (
        <Dialog
          title="Volver a borrador"
          busy={reverting}
          onClose={() => setShowRevert(false)}
          footer={(
            <>
              <button
                type="button"
                className="list-btn-secondary"
                onClick={() => setShowRevert(false)}
                disabled={reverting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="list-btn-primary"
                onClick={handleRevertToDraft}
                disabled={reverting}
              >
                {reverting ? 'Volviendo…' : 'Volver a borrador'}
              </button>
            </>
          )}
        >
          <p>
            El rack <strong>{formData.name}</strong> saldrá del inventario y volverá a ser
            un diseño. Conserva su código y la numeración ya consumida, y sus equipos
            montados no se tocan. Podrás pasarlo otra vez a producción cuando quieras.
          </p>
        </Dialog>
      )}

      {/* ── Confirmar borrado (diálogo común) ── */}
      {showDelete && (
        <Dialog
          title="Eliminar rack"
          busy={saving}
          onClose={() => setShowDelete(false)}
          footer={(
            <>
              <button className="list-btn-secondary" type="button" onClick={() => setShowDelete(false)} disabled={saving}>
                Cancelar
              </button>
              <button className="list-btn-danger" type="button" onClick={handleDelete} disabled={saving}>
                {saving ? 'Eliminando…' : 'Eliminar'}
              </button>
            </>
          )}
        >
          <p>
            ¿Seguro que quieres eliminar el rack <strong>{formData.name}</strong>? Se eliminarán
            también los elementos montados en él. Esta acción no se puede deshacer.
          </p>
        </Dialog>
      )}

      {/* ── SmartBar (solo en la ficha clásica) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        <button
          className="dv-smart-btn"
          type="button"
          onClick={() => setActiveTab('servidores')}
          title="Ver servidores en este rack"
        >
          <span className="dv-smart-btn-icon"><ServerStackIcon style={{ width: 18, height: 18, color: '#2563eb' }} /></span>
          <span className="dv-smart-btn-value">{servers.length}</span>
          <span className="dv-smart-btn-label">Servidores</span>
        </button>

        <button className="dv-smart-btn" type="button" title="Unidades ocupadas">
          <span className="dv-smart-btn-icon"><CubeIcon style={{ width: 18, height: 18, color: '#7c3aed' }} /></span>
          <span className="dv-smart-btn-value" style={{ color: '#7c3aed' }}>{occupancyPct}%</span>
          <span className="dv-smart-btn-label">Ocupación</span>
        </button>

        <button
          className="dv-smart-btn"
          type="button"
          onClick={() => setActiveTab('dimensiones')}
          title="Unidades libres"
        >
          <span className="dv-smart-btn-icon"><BoltIcon style={{ width: 18, height: 18, color: '#059669' }} /></span>
          <span className="dv-smart-btn-value" style={{ color: '#059669' }}>{freeU}U</span>
          <span className="dv-smart-btn-label">Libres</span>
        </button>

        <button className="dv-smart-btn" type="button" title="Altura total del rack">
          <span className="dv-smart-btn-value" style={{ color: '#0369a1' }}>{formData.height_u}U</span>
          <span className="dv-smart-btn-label">Altura total</span>
        </button>

        {isExisting && (
          <DocSmartButton elementType="rack" elementId={rack.id} elementName={formData.name} companyId={companyId} />
        )}

        {isExisting && (
          <button
            className="dv-smart-btn"
            type="button"
            onClick={() => navigate(`/infrastructure/rack/${rack.id}`)}
            title="Vista gráfica frontal y trasera del rack"
            style={{ marginLeft: 'auto' }}
          >
            <span className="dv-smart-btn-icon"><TableCellsIcon style={{ width: 18, height: 18, color: '#d97706' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#d97706' }}>Vista</span>
            <span className="dv-smart-btn-label">Gráfica</span>
          </button>
        )}
      </div>
      )}

      {/* ── Body ── */}
      <div className="dv-body">
        {/* Main panel */}
        <form className={`dv-main${isCards ? ' sf2-main' : ''}`} onSubmit={handleSubmit}>
          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <>
              <div className="sf2-head">
                <div className="sf2-head-icon" aria-hidden="true">
                  <RectangleStackIcon style={{ width: 20, height: 20 }} />
                </div>
                <div className="sf2-head-title">
                  <div className="sf2-title-row">
                    <input
                      className="sf2-head-input"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onKeyDown={handleTitleKeyDown}
                      style={{ '--sf2-title-len': (formData.name || '').length }}
                      aria-label="Nombre del rack"
                      placeholder="Nombre del rack…"
                      required
                    />
                    {/* En reposo el lápiz solo asoma al apuntar al título: es la pista
                        de que se puede editar, no un botón de modo. */}
                    {!titleActionsVisible && (
                      <button
                        type="button"
                        className="sf2-title-btn sf2-title-pencil"
                        onClick={(e) => e.currentTarget.parentElement.querySelector('.sf2-head-input')?.focus()}
                        title="Editar el nombre"
                        aria-label="Editar el nombre"
                      >
                        <PencilSquareIcon />
                      </button>
                    )}
                    {titleActionsVisible && (
                      <>
                        <button
                          type="button"
                          className="sf2-title-btn sf2-title-btn--ok"
                          onClick={commitTitle}
                          disabled={saving || !(nameDirty || isDirty)}
                          title="Guardar cambios"
                        >
                          {saving ? <span className="dv-spinner" /> : <CheckIcon />}
                        </button>
                        <button type="button" className="sf2-title-btn" onClick={handleDiscard} disabled={saving} title="Descartar cambios">
                          <XMarkIcon />
                        </button>
                      </>
                    )}
                    {/* Ayuda de la ficha: abre el tema en un cajón, sin salir. */}
                    <HelpDot topic="racks.form" label="Ayuda: la ficha del rack" />
                  </div>
                  <div className="sf2-crumb">
                    {[formData.code, `${servers.length} servidores`].filter(Boolean).join(' · ')}
                  </div>
                </div>

                {/* Estado arriba a la derecha, con el mismo badge que la ficha de
                    servidor (píldora con punto y colores propios). El cambio se
                    hace con el interruptor de la tarjeta Identificación. */}
                <div className="sf2-badges">
                  <span
                    className="sf2-badge"
                    style={formData.is_active
                      ? { background: '#dcfce7', color: '#15803d', borderColor: 'transparent' }
                      : { background: '#fee2e2', color: '#991b1b', borderColor: 'transparent' }}
                  >
                    <i style={{ background: formData.is_active ? '#15803d' : '#991b1b' }} />
                    {formData.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>

              {/* Fila de chips de la cabecera, igual que en el servidor: los
                  documentos van aquí y no en la barra de acciones. */}
              {isExisting && (
                <div className="sf2-chips">
                  <DocSmartButton
                    elementType="rack"
                    elementId={rack.id}
                    elementName={formData.name}
                    companyId={companyId}
                    variant="chip"
                  />
                </div>
              )}
              </>
            ) : (
              <input
                className="dv-record-title"
                name="name"
                value={formData.name}
                onChange={handleChange} disabled={!editing}
                placeholder="Nombre del rack…"
                required
              />
            )}
            tabs={(
              <div className="dv-tabs-nav">
                {tabs.map(tab => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`dv-tab-btn${activeTab === tab.key ? ' active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          >

          {/* ── Tab: General (solo tarjetas) ── */}
          {isCards && activeTab === 'general' && (
            <div className="dv-tab-pane">
              {/* Ocupación: un único bloque en lugar de cinco tarjetas sueltas. Los
                  valores son los mismos que usaba la tira de KPIs (occupiedU,
                  freeU, occupancyPct) y la altura del propio rack. */}
              <div className="sf2-card" style={{ marginBottom: 12 }}>
                <div className="sf2-occ">
                  <div className="sf2-occ-top">
                    <b className="sf2-occ-title">
                      {occupiedU}U de {formData.height_u || 0}U ocupadas
                    </b>
                    <span className="sf2-occ-pct">{occupancyPct} % de ocupación</span>
                  </div>
                  {formData.height_u > 60 ? (
                    /* Racks muy altos: barra continua con el porcentaje, que 90
                       segmentos no se leen. */
                    <div
                      className="sf2-occ-track"
                      role="img"
                      aria-label={`${occupiedU}U de ${formData.height_u}U ocupadas`}
                    >
                      <span className="sf2-occ-fill" style={{ width: `${occupancyPct}%` }} />
                    </div>
                  ) : (
                    <div
                      className="sf2-occ-bar"
                      role="img"
                      aria-label={`${occupiedU}U de ${formData.height_u || 0}U ocupadas`}
                    >
                      {Array.from({ length: formData.height_u || 0 }, (_, i) => (
                        <span
                          key={i}
                          className={`sf2-occ-seg${i < occupiedU ? ' is-on' : ''}`}
                          title={i < occupiedU ? `U${i + 1} · ocupada` : `U${i + 1} · libre`}
                        />
                      ))}
                    </div>
                  )}
                  <div className="sf2-occ-legend">
                    <span><b>{freeU}U</b> libres</span>
                    <span><b>{rackElements.length || servers.length}</b> elementos</span>
                    <span>Altura <b>{formData.height_u || 0}U</b></span>
                    <span
                      className="sf2-link"
                      role="button"
                      tabIndex={0}
                      onClick={() => setActiveTab('elementos')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveTab('elementos'); } }}
                    >Ver elementos del rack →</span>
                  </div>
                </div>
              </div>

              <div className="sf2-grid sf2-grid--2">
                <div className="sf2-card">
                  <h3>Identificación</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>Código</label>
                      {editing ? (
                        <div className="sf2-input-row">
                          <input name="code" value={formData.code || ''} onChange={handleChange} placeholder="R-BCN-01" />
                          {/* Generar código vive aquí, pegado al campo, como en la
                              ficha clásica; solo tiene sentido editando. */}
                          <button
                            type="button"
                            className="sf2-icon-btn"
                            onClick={handleSuggest}
                            disabled={suggesting || !companyId || saving}
                            title="Generar código y nombre a partir de la ubicación"
                            aria-label="Generar código a partir de la ubicación"
                          >
                            {suggesting ? <span className="dv-spinner" /> : <SparklesIcon style={{ width: 15, height: 15 }} />}
                          </button>
                        </div>
                      ) : <div className="v">{formData.code || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Nombre</label>
                      {editing ? (
                        <input name="name" value={formData.name || ''} onChange={handleChange} required />
                      ) : <div className="v">{formData.name || '—'}</div>}
                    </div>
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Ubicación</label>
                      {editing ? (
                        <LocationTreeSelect
                          locations={locations}
                          value={formData.location_id || ''}
                          onChange={(id) => { setFormData(prev => ({ ...prev, location_id: id })); setIsDirty(true); }}
                          placeholder="— Selecciona ubicación —"
                        />
                      ) : (
                        <div className="v">{ubicacionRuta || '—'}</div>
                      )}
                    </div>
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Descripción</label>
                      {editing ? (
                        <textarea
                          className="dv-input"
                          rows={2}
                          name="description"
                          value={formData.description || ''}
                          onChange={handleChange}
                          placeholder="Notas del rack…"
                        />
                      ) : <div className="v">{formData.description || '—'}</div>}
                    </div>
                    {/* El badge de la cabecera solo informa; aquí se cambia, igual
                        que el campo «Estado» de la ficha de servidor. */}
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <div className="sf2-field-switches">
                        <label className="sf2-switch sf2-switch--sm" title="Si el rack está activo en el inventario">
                          <input
                            type="checkbox"
                            name="is_active"
                            checked={!!formData.is_active}
                            onChange={handleChange}
                            disabled={!editing}
                          />
                          <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                          <span className="sf2-switch-label">{formData.is_active ? 'Activo' : 'Inactivo'}</span>
                        </label>
                        {/* «Es un diseño» solo se elige al darlo de alta: el estado de
                            un rack ya creado no se cambia editando (el backend lo
                            rechaza), sale por `/promote`. Un interruptor deshabilitado
                            que no hace nada es peor que no ponerlo. */}
                        {!isExisting ? (
                          <label
                            className="sf2-switch sf2-switch--sm"
                            title="Un diseño no entra en inventario ni consume numeración hasta pasarlo a producción"
                          >
                            <input
                              type="checkbox"
                              checked={formData.status === 'draft'}
                              disabled={!editing}
                              onChange={(e) => {
                                setFormData(prev => ({ ...prev, status: e.target.checked ? 'draft' : 'live' }));
                                setIsDirty(true);
                              }}
                            />
                            <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                            <span className="sf2-switch-label">Es un diseño</span>
                          </label>
                        ) : (
                          <>
                            <span className={`sf2-pill${formData.status === 'draft' ? '' : ' sf2-pill--ok'}`}>
                              {formData.status === 'draft' ? 'Borrador' : 'En producción'}
                            </span>
                            {formData.status !== 'draft' && (
                              <button
                                type="button"
                                className="sf2-link"
                                onClick={() => setShowRevert(true)}
                              >
                                Volver a borrador →
                              </button>
                            )}
                          </>
                        )}
                      </div>
                      <span className="sf2-hint">
                        {!isExisting
                          ? 'Un diseño no entra en inventario ni consume numeración hasta pasarlo a producción.'
                          : formData.status === 'draft'
                            ? (
                              <>
                                Es un borrador: no consume numeración hasta pasarlo a producción.{' '}
                                <button type="button" className="sf2-link" onClick={() => setShowPromote(true)}>
                                  Pasar a producción →
                                </button>
                              </>
                            )
                            : 'En producción cuenta en el inventario. Volver a borrador lo saca de él sin perder el código ni la numeración.'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Columna derecha de la ficha: Dimensiones y, justo debajo,
                    Hardware. Las dos comparten el ancho de la columna, no el de
                    la ficha completa. */}
                <div className="sf2-col-stack">
                  <div className="sf2-card">
                    <h3>Dimensiones</h3>
                    <div className="sf2-fields" style={{ '--n': 3 }}>
                      <div className="sf2-field">
                        <label>Alto (U)</label>
                        {editing ? (
                          <input type="number" min="1" name="height_u" value={formData.height_u} onChange={handleChange} />
                        ) : <div className="v">{formData.height_u || '—'}</div>}
                      </div>
                      <div className="sf2-field">
                        <label>Ancho (mm)</label>
                        {editing ? (
                          <input type="number" name="width_mm" value={formData.width_mm} onChange={handleChange} />
                        ) : <div className="v">{formData.width_mm || '—'}</div>}
                      </div>
                      <div className="sf2-field">
                        <label>Fondo (mm)</label>
                        {editing ? (
                          <input type="number" name="depth_mm" value={formData.depth_mm} onChange={handleChange} />
                        ) : <div className="v">{formData.depth_mm || '—'}</div>}
                      </div>
                    </div>
                  </div>

                  <div className="sf2-card">
                    <h3>Hardware</h3>
                    <div className="sf2-fields" style={{ '--n': 2 }}>
                      <div className="sf2-field">
                        <label>Fabricante</label>
                        {editing ? (
                          <input name="manufacturer" value={formData.manufacturer || ''} onChange={handleChange} />
                        ) : <div className="v">{formData.manufacturer || '—'}</div>}
                      </div>
                      <div className="sf2-field">
                        <label>Modelo</label>
                        {editing ? (
                          <input name="model" value={formData.model || ''} onChange={handleChange} />
                        ) : <div className="v">{formData.model || '—'}</div>}
                      </div>
                      <div className="sf2-field">
                        <label>Nº de serie</label>
                        {editing ? (
                          <input name="serial_number" value={formData.serial_number || ''} onChange={handleChange} />
                        ) : <div className="v">{formData.serial_number || '—'}</div>}
                      </div>
                      <div className="sf2-field">
                        <label>Grupo de diseño</label>
                        {editing ? (
                          <input name="design_group" value={formData.design_group || ''} onChange={handleChange} />
                        ) : <div className="v">{formData.design_group || '—'}</div>}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* ── Tab: Elementos del rack (solo tarjetas) ── */}
          {isCards && activeTab === 'elementos' && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              <div className="dv-section">
                <div className="dv-section-title">Elementos instalados en el rack</div>
                {(rackElements.length === 0 && servers.length === 0) ? (
                  <div className="sf2-empty">No hay elementos asignados a este rack.</div>
                ) : (
                  <div className="sf2-table-wrap">
                    <table className="sf2-table">
                      <thead>
                        <tr>
                          <th>Tipo</th>
                          <th>Código</th>
                          <th>Nombre</th>
                          <th>Posición (U)</th>
                          <th>Tamaño</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(rackElements.length ? rackElements : servers).map((el) => (
                          <tr key={el.id}>
                            <td>{ELEMENT_TYPE_LABEL[el.element_type] || el.element_type_label || el.element_type || 'Servidor'}</td>
                            <td><code>{el.code || '—'}</code></td>
                            <td>{el.name}</td>
                            <td>{el.position_u ?? '—'}</td>
                            <td>{el.size_u ?? el.height_u ?? 1}U</td>
                            <td>
                              <span className={`sf2-pill ${el.is_active === false ? '' : 'sf2-pill--ok'}`}>
                                {el.is_active === false ? 'Inactivo' : 'Activo'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Tab: General (solo ficha clásica) ── */}
          {!isCards && activeTab === 'general' && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              <div className="dv-section">
                <div className="dv-section-title">Identificación</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Código <span className="text-danger">*</span></label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        name="code"
                        value={formData.code}
                        onChange={handleChange} disabled={!editing}
                        required
                        autoFocus={!isExisting}
                        placeholder="R-BCN-01"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleSuggest}
                        disabled={suggesting || !companyId}
                        title="Sugerir código automático"
                      >
                        {suggesting ? '…' : <SparklesIcon style={{ width: 16, height: 16 }} />}
                      </button>
                    </div>
                  </div>

                  <div className="col-md-5">
                    <label className="form-label">
                      Ubicación {formData.status !== 'draft' && <span className="text-danger">*</span>}
                    </label>
                    <LocationTreeSelect
                      locations={locations}
                      value={formData.location_id || ''}
                      onChange={(id) => handleChange({ target: { name: 'location_id', value: id } })}
                      placeholder="— Selecciona ubicación —"
                      disabled={!editing}
                    />
                  </div>

                  <div className="col-md-3 d-flex align-items-end">
                    <div className="mb-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="is_active"
                          id="dv_is_active"
                          checked={formData.is_active}
                          onChange={handleChange} disabled={!editing}
                        />
                        <label className="form-check-label fw-semibold" htmlFor="dv_is_active">
                          Activo
                        </label>
                      </div>
                      {isExisting ? (
                        <div className="form-check d-flex align-items-center gap-2">
                          <span className={`badge ${formData.status === 'draft' ? 'bg-warning text-dark' : 'bg-success'}`}>
                            {formData.status === 'draft' ? 'Borrador' : 'En producción'}
                          </span>
                          {formData.status !== 'draft' && (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0"
                              onClick={() => setShowRevert(true)}
                            >
                              Volver a borrador
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="dv_is_draft"
                            checked={formData.status === 'draft'}
                            disabled={!editing}
                            onChange={(e) => {
                              setFormData(prev => ({
                                ...prev,
                                status: e.target.checked ? 'draft' : 'live',
                              }));
                              setIsDirty(true);
                            }}
                          />
                          <label className="form-check-label fw-semibold" htmlFor="dv_is_draft">
                            Es un diseño
                          </label>
                        </div>
                      )}
                    </div>
                  </div>

                  {formData.status === 'draft' && (
                    <div className="col-12">
                      <label className="form-label">Grupo de diseño</label>
                      <input
                        className="form-control"
                        name="design_group"
                        value={formData.design_group || ''}
                        onChange={handleChange} disabled={!editing}
                        placeholder="Cliente X · Fase 1 · Sala 2"
                      />
                      <div className="form-text">
                        Agrupa varios armarios del mismo proyecto. El armario no entra
                        en inventario ni consume numeración hasta que lo pases a producción.
                      </div>
                    </div>
                  )}

                  <div className="col-12">
                    <label className="form-label">Descripción</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={formData.description || ''}
                      onChange={handleChange} disabled={!editing}
                      rows={2}
                      placeholder="Descripción o notas rápidas sobre este rack…"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Dimensiones ── */}
          {!isCards && activeTab === 'dimensiones' && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              <div className="dv-section">
                <div className="dv-section-title">Medidas del rack</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Altura (U)</label>
                    <input
                      type="number"
                      className="form-control"
                      name="height_u"
                      value={formData.height_u}
                      onChange={handleChange} disabled={!editing}
                      min="1"
                    />
                    <div className="form-text">Unidades de rack estándar (1U = 44,45 mm)</div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Anchura (mm)</label>
                    <input
                      type="number"
                      className="form-control"
                      name="width_mm"
                      value={formData.width_mm}
                      onChange={handleChange} disabled={!editing}
                    />
                    <div className="form-text">Estándar: 600 mm</div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Profundidad (mm)</label>
                    <input
                      type="number"
                      className="form-control"
                      name="depth_mm"
                      value={formData.depth_mm}
                      onChange={handleChange} disabled={!editing}
                    />
                    <div className="form-text">Estándar: 800 mm</div>
                  </div>
                </div>
              </div>

              {/* occupancy summary */}
              {isExisting && (
                <div className="dv-section">
                  <div className="dv-section-title">Ocupación actual</div>
                  <div className="d-flex align-items-center gap-3">
                    <div style={{ flex: 1, background: 'rgba(255,255,255,.07)', borderRadius: 6, height: 12, overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%',
                          width: `${occupancyPct}%`,
                          background: occupancyPct > 85 ? '#dc2626' : occupancyPct > 65 ? '#f59e0b' : '#22c55e',
                          transition: 'width 0.4s',
                        }}
                      />
                    </div>
                    <span style={{ fontSize: '0.8rem', color: '#475569', whiteSpace: 'nowrap' }}>
                      {occupiedU}U ocupadas · {freeU}U libres ({occupancyPct}%)
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Hardware ── */}
          {!isCards && activeTab === 'hardware' && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              <div className="dv-section">
                <div className="dv-section-title">Información del fabricante</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Fabricante</label>
                    <input
                      type="text"
                      className="form-control"
                      name="manufacturer"
                      value={formData.manufacturer || ''}
                      onChange={handleChange} disabled={!editing}
                      placeholder="APC / HPE / Rittal"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Modelo</label>
                    <input
                      type="text"
                      className="form-control"
                      name="model"
                      value={formData.model || ''}
                      onChange={handleChange} disabled={!editing}
                      placeholder="NetShelter SX"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Número de serie</label>
                    <input
                      type="text"
                      className="form-control"
                      name="serial_number"
                      value={formData.serial_number || ''}
                      onChange={handleChange} disabled={!editing}
                      placeholder="SN123456"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Servidores ── */}
          {!isCards && activeTab === 'servidores' && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              {servers.length === 0 ? (
                <div className="dv-empty-state">
                  <ServerStackIcon style={{ width: 40, height: 40, margin: '0 auto 0.5rem', display: 'block', color: '#cbd5e1' }} />
                  <p>No hay servidores asignados a este rack.</p>
                </div>
              ) : (
                <table className="dv-servers-table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Nombre</th>
                      <th>Posición (U)</th>
                      <th>Tamaño (U)</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {servers.map(srv => (
                      <tr key={srv.id}>
                        <td><code style={{ fontSize: '0.78rem' }}>{srv.code}</code></td>
                        <td>{srv.name}</td>
                        <td>{srv.position_u ?? '—'}</td>
                        <td>{srv.size_u ?? srv.height_u ?? 1}U</td>
                        <td>
                          <span style={{
                            padding: '0.15rem 0.5rem',
                            borderRadius: 99,
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            background: srv.is_active !== false ? '#dcfce7' : '#fee2e2',
                            color: srv.is_active !== false ? '#15803d' : '#991b1b',
                          }}>
                            {srv.is_active !== false ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
          <FormDirtyDock
            show={isCards && (isDirty || nameDirty)}
            saving={saving}
            onDiscard={handleDiscard}
            onSave={commitTitle}
          />
          </FormCardsShell>
        </form>

        {/* ── Right sidebar: Chatter ── */}
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="rack"
          ciId={rack?.id}
          tipoLegible="el rack"
        />
      </div>
    </div>
  );
}

export default RackFormElegant;
